import { useState, useRef } from "react";
import { Upload, FileText, CheckCircle, AlertTriangle, X, ChevronDown } from "lucide-react";

/* ─── Parse helpers ─── */
const parseTextToQuestions = (text) => {
  const lines = text.split("\n").map(l => l.trim()).filter(l => l);
  const questions = [];
  let currentQ = null;

  for (const line of lines) {
    // Question line: starts with "1." or "1)" or "ข้อ 1."
    if (/^(\d+)[.)]\s+/.test(line) || /^ข้อ\s*\d+[.):\s]/.test(line)) {
      if (currentQ && currentQ.text) questions.push(currentQ);
      currentQ = {
        id: Date.now() + questions.length + Math.random(),
        type: "ปรนัย",
        text: line.replace(/^(\d+)[.)]\s+/, "").replace(/^ข้อ\s*\d+[.):\s]+/, "").trim(),
        options: [],
        score: 3,
        correctOption: 0,
      };
    } else if (/^[a-dA-D][.)]\s+/.test(line) || /^[กขคง][.)]\s+/.test(line)) {
      if (currentQ) {
        currentQ.options.push(line.replace(/^[a-dA-Dกขคง][.)]\s+/, "").trim());
      }
    } else if (line.length > 0 && currentQ && currentQ.options.length === 0) {
      // Continuation of question text
      if (currentQ.text) currentQ.text += " " + line;
    }
  }
  if (currentQ && currentQ.text) questions.push(currentQ);

  // Set essay type for questions without options
  return questions.map(q => ({
    ...q,
    type: q.options.length === 0 ? "อัตนัย" : "ปรนัย",
  }));
};

const parseSheetToQuestions = (data) => {
  const result = [];
  // Auto-detect header row
  const startRow = data[0] && typeof data[0][0] === "string" && isNaN(Number(data[0][0])) && data[0][0].length < 5 ? 1 : 0;

  for (let i = startRow; i < data.length; i++) {
    const row = data[i];
    if (!row || !row[0]) continue;
    const opts = [row[1], row[2], row[3], row[4]].map(v => (v !== undefined && v !== null ? String(v).trim() : "")).filter(v => v);
    result.push({
      id: Date.now() + i + Math.random(),
      type: opts.length === 0 ? "อัตนัย" : "ปรนัย",
      text: String(row[0]).trim(),
      options: opts,
      score: Number(row[5]) || 3,
      correctOption: 0,
    });
  }
  return result;
};

/* ─── Sample Questions (shown when file parse fails or as demo) ─── */
const SAMPLE_QUESTIONS = [
  { id: 1, type: "ปรนัย", text: "ใครเป็นผู้พัฒนาทฤษฎี Two-Step Flow of Communication?", options: ["Harold Lasswell", "Paul Lazarsfeld และ Elihu Katz", "Marshall McLuhan", "Walter Lippmann"], score: 3, correctOption: 0 },
  { id: 2, type: "ปรนัย", text: "แนวคิด The Medium is the Message เป็นของนักทฤษฎีท่านใด?", options: ["Noam Chomsky", "Stuart Hall", "Marshall McLuhan", "Jürgen Habermas"], score: 3, correctOption: 0 },
  { id: 3, type: "อัตนัย", text: "จงอธิบายความแตกต่างระหว่างทฤษฎี One-Step Flow และ Two-Step Flow พร้อมยกตัวอย่าง", options: [], score: 5, correctOption: 0 },
];

/* ─── Main Exam Importer Component ─── */
export default function ExamImporter({ onImport, onClose }) {
  const [step, setStep] = useState("upload"); // 'upload' | 'review'
  const [parsing, setParsing] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setError("");

    try {
      const ext = file.name.split(".").pop().toLowerCase();
      let parsed = [];

      if (ext === "docx") {
        const mammoth = (await import("mammoth")).default;
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        parsed = parseTextToQuestions(result.value);
      } else if (ext === "xlsx" || ext === "xls") {
        const XLSX = await import("xlsx");
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        parsed = parseSheetToQuestions(data);
      } else if (ext === "doc") {
        setError("ไฟล์ .doc ไม่รองรับการแปลงโดยตรง — กรุณาบันทึกใหม่เป็น .docx แล้วลองอีกครั้ง");
        setParsing(false);
        return;
      } else {
        setError("รองรับเฉพาะไฟล์ .docx, .xlsx หรือ .xls");
        setParsing(false);
        return;
      }

      if (parsed.length === 0) {
        setError("ไม่พบข้อสอบในไฟล์ — ใช้ข้อมูลตัวอย่างแทน");
        parsed = SAMPLE_QUESTIONS;
      }

      setQuestions(parsed);
      setStep("review");
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการอ่านไฟล์: " + err.message + " — ใช้ข้อมูลตัวอย่างแทน");
      setQuestions(SAMPLE_QUESTIONS);
      setStep("review");
    }
    setParsing(false);
  };

  const setCorrect = (qIdx, optIdx) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, correctOption: optIdx } : q));
  };

  const setScore = (qIdx, score) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, score: Number(score) || 3 } : q));
  };

  const handleConfirm = () => {
    onImport(questions);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.4)", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-[#1A1B1F] text-lg flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}>
            <Upload size={18} strokeWidth={1.5} className="text-[#007CB7]" />
            {step === "upload" ? "นำเข้าข้อสอบจากไฟล์" : `ตรวจสอบข้อสอบ — พบ ${questions.length} ข้อ`}
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Upload Step */}
          {step === "upload" && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700 space-y-1">
                <p className="font-semibold">รูปแบบไฟล์ที่รองรับ</p>
                <p>• <strong>.docx</strong> — ข้อความในรูปแบบ "1. คำถาม" และ "a. ตัวเลือก"</p>
                <p>• <strong>.xlsx / .xls</strong> — ตาราง: คอลัมน์ A=คำถาม, B=ตัวเลือก A, C=ตัวเลือก B, D=ตัวเลือก C, E=ตัวเลือก D</p>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-700">
                  <AlertTriangle size={16} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#007CB7] hover:bg-blue-50 transition-all"
                onClick={() => fileRef.current?.click()}
              >
                {parsing ? (
                  <div className="space-y-2">
                    <div className="w-10 h-10 border-4 border-[#007CB7] border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-[#5E636E]">กำลังประมวลผลไฟล์...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto">
                      <FileText size={28} strokeWidth={1.5} className="text-[#007CB7]" />
                    </div>
                    <p className="text-sm font-medium text-[#1A1B1F]">คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวาง</p>
                    <p className="text-xs text-[#8A92A3]">.docx, .xlsx, .xls</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".docx,.xlsx,.xls,.doc" className="hidden" onChange={handleFile} data-testid="exam-file-input" />
              </div>

              <div className="flex justify-center">
                <button onClick={() => { setQuestions(SAMPLE_QUESTIONS); setStep("review"); setFileName("ข้อมูลตัวอย่าง"); }}
                  className="text-sm text-[#007CB7] hover:underline">
                  หรือใช้ข้อมูลตัวอย่างแทน →
                </button>
              </div>
            </>
          )}

          {/* Review Step */}
          {step === "review" && (
            <>
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-4 py-2 text-sm text-green-700">
                <CheckCircle size={15} strokeWidth={1.5} />
                โหลดจาก: <strong>{fileName}</strong> — พบ {questions.length} ข้อสอบ
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                  <AlertTriangle size={14} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />{error}
                </div>
              )}

              <p className="text-sm font-semibold text-[#5E636E] uppercase tracking-wide">กรุณาเลือกคำตอบที่ถูกต้องสำหรับแต่ละข้อ</p>

              <div className="space-y-4">
                {questions.map((q, idx) => (
                  <div key={q.id || idx} className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#007CB7] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${q.type === "ปรนัย" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>{q.type}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[#5E636E]">คะแนน:</span>
                            <input type="number" min="1" max="100" value={q.score} onChange={e => setScore(idx, e.target.value)}
                              className="w-12 text-xs border border-gray-300 rounded px-1.5 py-0.5 text-center focus:ring-1 focus:ring-[#007CB7] outline-none" />
                          </div>
                        </div>
                        <p className="text-sm text-[#1A1B1F] leading-relaxed">{q.text}</p>
                      </div>
                    </div>

                    {q.type === "ปรนัย" && q.options.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-10">
                        {q.options.map((opt, i) => (
                          <label key={`${idx}-opt-${i}`} data-testid={`import-option-${idx}-${i}`}
                            className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${q.correctOption === i ? "border-green-400 bg-green-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}>
                            <input type="radio" name={`q-correct-${idx}`} checked={q.correctOption === i} onChange={() => setCorrect(idx, i)} className="w-4 h-4 text-[#007CB7] flex-shrink-0" />
                            <span className="font-semibold text-[#5E636E] text-xs w-5 flex-shrink-0">{String.fromCharCode(65 + i)}.</span>
                            <span className="text-[#1A1B1F]">{opt}</span>
                            {q.correctOption === i && <CheckCircle size={14} strokeWidth={1.5} className="text-green-500 ml-auto flex-shrink-0" />}
                          </label>
                        ))}
                      </div>
                    )}
                    {q.type === "อัตนัย" && (
                      <div className="ml-10 text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded-md px-3 py-1.5">คำถามอัตนัย — ไม่ต้องเลือกคำตอบ</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
          {step === "review" ? (
            <>
              <button onClick={() => { setStep("upload"); setError(""); }} className="flex items-center gap-2 text-sm text-[#5E636E] hover:text-[#1A1B1F]">
                <ChevronDown size={14} strokeWidth={1.5} className="rotate-90" />เลือกไฟล์ใหม่
              </button>
              <button data-testid="confirm-import-btn" onClick={handleConfirm}
                className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-6 py-2.5 rounded-md text-sm font-semibold transition-colors">
                <CheckCircle size={15} strokeWidth={1.5} />ยืนยันนำเข้า {questions.length} ข้อสอบ
              </button>
            </>
          ) : (
            <div className="flex-1 flex justify-end">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-white transition-colors">ยกเลิก</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
